import React, { Fragment, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  getJobSettingsState,
  getResultState,
  getRoofState,
  getSettingsState,
  getWallState,
} from '../../../store/slices/selectors'
import { resultActions } from '../../../store/slices'

import dayjs from 'dayjs'

import TableView from './TableView'
import Tooltip from 'react-bootstrap/Tooltip'
import { CSVLink } from 'react-csv'
import { Button, Popper } from '../../../../../components'

import {
  displayFastener,
  getAllThreadRobLabel,
  getHeelMaxHeight,
  lbsToPlf,
  toPercentage,
} from '../../../common/fns'
import { APPLICATION_TYPES, MEMBER_TYPES, ROOF_MEMBER_TYPES } from '../../../common/constants'
import OUTPUT_HELPER_DATA from '../../../common/json/OUTPUT_HELPER_DATA.json'
import { RenderAorB } from '../../../../../utils/fns'

const STEAL_THREADED_ROD_PAGE = 'https://www.strongtie.com/atscomponents_anchortiedownsystem/atsthreadedrod_productgroup_lat/p/steel-threaded-rod'

const getTableRowData = (item) => {
  const additionalHardware = item.second.model.model
  return {
    model: item.model.model,
    configuration: item.installation.configuration,
    imageInstallation1Url: item.installation.imageInstallation1Url,
    ici: item.installation.ici,
    quantity: item.installation.qtyPerUse,
    f1: item.capacity.f1,
    f2Away: item.capacity.f2away,
    f2Toward: item.capacity.f2toward,
    uplift: item.capacity.uplift,
    additionalHardware,
    additionalHardwareQuantity: additionalHardware === 'N/A' ? 'N/A' : item.second.quantity,
    additionalHardwareFastener:
      additionalHardware === 'N/A'
        ? 'N/A'
        : displayFastener(item.second.rafterNail.nailQty, item.second.rafterNail.nailType),
    allThreadRod: getAllThreadRobLabel(item.second.wallNail.nailQty, item.second.wallNail.nailType),
    wallFasteners: displayFastener(item.wallNail.nailQty, item.wallNail.nailType),
    trussFasteners: displayFastener(item.rafterNail.nailQty, item.rafterNail.nailType),
    download: item.capacity.load,
  }
}

const Result = () => {
  const dispatch = useDispatch()

  const [tableData, setTableData] = useState([])

  const isUSA = useSelector((state) => getSettingsState(state).isUSA)
  const applicationType = useSelector((state) => getWallState(state).applicationType)
  const studAlignment = useSelector((state) => getWallState(state).studAlignment)
  const wallLength = useSelector((state) => getJobSettingsState(state).wallLength)
  const wallMemberType = useSelector((state) => getWallState(state).memberType)
  const { data, optimized, searchKeyword, selectedModel } = useSelector(getResultState)
  const { memberType, heel, topChordDepth, topSlope } = useSelector(getRoofState)

  const formattedData = useMemo(
    () => (data ? data.map(getTableRowData).sort((a, b) => a.ici - b.ici) : []),
    [data],
  )

  const columns = useMemo(() => {
    const resultColumns = [
      {
        Header: 'Model',
        accessor: 'model',
        style: { width: 128 },
        sticky: 'left',
        // eslint-disable-next-line react/prop-types
        Cell: ({ value, row, toggleAllRowsSelected }) => {
          // eslint-disable-next-line react/prop-types
          const { imageInstallation1Url } = row.original
          const redirectUrl = OUTPUT_HELPER_DATA.modelUrls[value]
          return (
            <div className="modelCellContent">
              <a target="_blank" href={redirectUrl} rel="noreferrer">
                {value}
              </a>
              {imageInstallation1Url && (
                <div className="modelCellContent__image">
                  {RenderAorB(
                    // eslint-disable-next-line react/prop-types
                    row.isSelected,
                    <button
                      onClick={() => {
                        toggleAllRowsSelected(false)
                        // eslint-disable-next-line react/prop-types
                        row.toggleRowSelected(false)
                        // eslint-disable-next-line react/prop-types
                        dispatch(resultActions.setSelectedModel(null))
                      }}
                      // eslint-disable-next-line react/prop-types
                      className="modelSelected"
                    >
                      <img src={imageInstallation1Url} />
                    </button>,
                    <Popper
                      placement="right"
                      trigger={['hover', 'focus']}
                      overlay={<Tooltip className="-model bs-tooltip-right">Show in 3D</Tooltip>}
                    >
                      <button
                        onClick={() => {
                          toggleAllRowsSelected(false)
                          // eslint-disable-next-line react/prop-types
                          row.toggleRowSelected(true)
                          // eslint-disable-next-line react/prop-types
                          dispatch(resultActions.setSelectedModel({ ...row.original, id: row.id }))
                        }}
                      >
                        <img src={imageInstallation1Url} />
                      </button>
                    </Popper>,
                  )}
                </div>
              )}
            </div>
          )
        },
      },
      {
        Header: 'Installed Cost',
        accessor: 'ici',
        // eslint-disable-next-line react/prop-types
        Cell: ({ value }) => (
          <span>
            {value === 999999 ? '-' : value === 100 ? 'Lowest' : `+${toPercentage(value - 100)}`}
          </span>
        ),
      },
      {
        Header: 'Quantity',
        accessor: 'quantity',
      },
      {
        Header: 'Wall Fasteners',
        accessor: 'wallFasteners',
      },
      {
        Header: 'Truss/Rafter Fasteners',
        accessor: 'trussFasteners',
      },
      ...(applicationType === APPLICATION_TYPES.PERPENDICULAR_TO_WALL
        ? [
            ...(studAlignment && wallMemberType === MEMBER_TYPES.STUD
              ? [
                  {
                    Header: 'Additional Hardware',
                    accessor: 'additionalHardware',
                  },
                  {
                    Header: 'Additional Hardware Quantity',
                    accessor: 'additionalHardwareQuantity',
                  },
                  {
                    Header: 'Additional Hardware Fasteners',
                    accessor: 'additionalHardwareFastener',
                  },
                ]
              : []),
            {
              Header: 'All Thread Rod',
              accessor: 'allThreadRod',
              Cell: ({ value }) =>
                value === 'N/A' ? (
                  value
                ) : (
                  <a
                    target="_blank"
                    href={STEAL_THREADED_ROD_PAGE}
                    rel="noreferrer"
                  >
                    {value}
                  </a>
                ),
            },
          ]
        : []),
      {
        Header: `F1 (${applicationType === APPLICATION_TYPES.PARALLEL_TO_WALL ? 'plf' : 'lbs'})`,
        accessor: 'f1',
        // eslint-disable-next-line react/prop-types
        Cell: ({ value }) => (
          <span>
            {applicationType === APPLICATION_TYPES.PARALLEL_TO_WALL
              ? lbsToPlf(value, wallLength)
              : value}
          </span>
        ),
      },
      ...(applicationType !== APPLICATION_TYPES.ANGLE_TO_WALL
        ? [
            {
              Header: `F2 ${
                applicationType === APPLICATION_TYPES.PARALLEL_TO_WALL ? 'Away (plf)' : '(lbs)'
              }`,
              accessor: 'f2Away',
              // eslint-disable-next-line react/prop-types
              Cell: ({ value }) => (
                <span>
                  {applicationType === APPLICATION_TYPES.PARALLEL_TO_WALL
                    ? lbsToPlf(value, wallLength)
                    : value}
                </span>
              ),
            },
          ]
        : []),
      ...(applicationType === APPLICATION_TYPES.PARALLEL_TO_WALL
        ? [
            {
              Header: 'F2 Toward (plf)',
              accessor: 'f2Toward',
              // eslint-disable-next-line react/prop-types
              Cell: ({ value }) => (
                <span>
                  {applicationType === APPLICATION_TYPES.PARALLEL_TO_WALL
                    ? lbsToPlf(value, wallLength)
                    : value}
                </span>
              ),
            },
          ]
        : []),
      {
        Header: `Uplift (${
          applicationType !== APPLICATION_TYPES.PARALLEL_TO_WALL ? 'lbs' : 'plf'
        })`,
        accessor: 'uplift',
        // eslint-disable-next-line react/prop-types
        Cell: ({ value }) => (
          <span>
            {applicationType === APPLICATION_TYPES.PARALLEL_TO_WALL
              ? lbsToPlf(value, wallLength)
              : value}
          </span>
        ),
      },
      ...(applicationType === APPLICATION_TYPES.PERPENDICULAR_TO_WALL
        ? [
            {
              Header: `Download (lbs)`,
              accessor: 'download',
            },
          ]
        : []),
    ]
    return resultColumns
  }, [applicationType, isUSA])

  const getTableData = (value) => {
    setTableData(value)
  }

  const csv = useMemo(() => {
    return tableData?.map(
      ({
        model,
        ici,
        quantity,
        wallFasteners,
        trussFasteners,
        additionalHardware,
        additionalHardwareQuantity,
        additionalHardwareFastener,
        allThreadRod,
        f1,
        f2Away,
        f2Toward,
        uplift,
        download,
      }) => {
        return {
          Model: model,
          'Installed Cost':
            ici === 999999 ? '-' : ici === 100 ? 'Lowest' : `+ ${toPercentage(ici - 100)}`,
          Quantity: quantity,
          'Wall Fasteners': `${wallFasteners}`,
          'Truss/Rafter Fasteners': `${trussFasteners}`,
          ...(applicationType === APPLICATION_TYPES.PERPENDICULAR_TO_WALL
            ? {
                ...(studAlignment && wallMemberType === MEMBER_TYPES.STUD
                  ? {
                      'Additional Hardware': additionalHardware,
                      'Additional Hardware Quantity': additionalHardwareQuantity,
                      'Additional Hardware Fasteners': `${additionalHardwareFastener}`,
                    }
                  : {}),
                'All Thread Rod': `${allThreadRod}`,
              }
            : null),
          [`F1 (${applicationType === APPLICATION_TYPES.PARALLEL_TO_WALL ? 'plf' : 'lbs'})`]:
            applicationType === APPLICATION_TYPES.PARALLEL_TO_WALL ? lbsToPlf(f1, wallLength) : f1,
          ...(applicationType !== APPLICATION_TYPES.ANGLE_TO_WALL
            ? {
                [`F2 ${
                  applicationType === APPLICATION_TYPES.PARALLEL_TO_WALL ? 'Away (plf)' : '(lbs)'
                }`]:
                  applicationType === APPLICATION_TYPES.PARALLEL_TO_WALL
                    ? lbsToPlf(f2Away, wallLength)
                    : f2Away,
              }
            : null),
          ...(applicationType === APPLICATION_TYPES.PARALLEL_TO_WALL
            ? {
                'F2 Toward (plf)':
                  applicationType === APPLICATION_TYPES.PARALLEL_TO_WALL
                    ? lbsToPlf(f2Toward, wallLength)
                    : f2Toward,
              }
            : null),
          [`Uplift (${applicationType !== APPLICATION_TYPES.PARALLEL_TO_WALL ? 'lbs' : 'plf'})`]:
            applicationType === APPLICATION_TYPES.PARALLEL_TO_WALL
              ? lbsToPlf(uplift, wallLength)
              : uplift,

          ...(applicationType === APPLICATION_TYPES.PERPENDICULAR_TO_WALL
            ? { 'Download (lbs)': download }
            : null),
        }
      },
    )
  }, [tableData, applicationType, isUSA, studAlignment])

  const getRowId = (row, relativeIndex, parent) => {
    return JSON.stringify(row)
  }

  const selectedRowIds = useMemo(() => {
    return selectedModel ? { [selectedModel.id]: true } : {}
  }, [selectedModel])

  const hiddenColumns = useMemo(
    () =>
      !studAlignment
        ? ['additionalHardware', 'additionalHardwareQuantity', 'additionalHardwareFastener']
        : [],
    [studAlignment],
  )

  const setOptimized = () => dispatch(resultActions.setOptimized())
  const setSearchKeyword = (value) => dispatch(resultActions.setSearchKeyword(value))

  return (
    <Fragment>
      <div className="d-flex justify-content-end">
        {csv && csv.length > 0 ? (
          <CSVLink
            className="button -tertiary -xsmall"
            data={csv}
            filename={`rws-output-${dayjs().format('YYYYMMDD')}.csv`}
            enclosingCharacter={''}
          >
            DOWNLOAD CSV
          </CSVLink>
        ) : (
          <Button className="button -xsmall" disabled style={{ cursor: 'not-allowed' }}>
            DOWNLOAD CSV
          </Button>
        )}
      </div>
      <TableView
        columns={columns}
        data={formattedData}
        optimized={optimized}
        setOptimized={setOptimized}
        searchKeyword={searchKeyword}
        setSearchKeyword={setSearchKeyword}
        getTableData={getTableData}
        getRowId={getRowId}
        selectedRowIds={selectedRowIds}
        hiddenColumns={hiddenColumns}
      />
      <div className="tableNotesWrapper">
        <h4>Table Notes</h4>
        {memberType === ROOF_MEMBER_TYPES.RAFTER &&
          getHeelMaxHeight(topChordDepth, topSlope) > heel && (
            <p>
              *Designer should evaluate the placement and size of the notch to ensure that it does
              not compromise the structural integrity of the wood member.
            </p>
          )}
        <ol className="space-y-2">
          <li>{`All loads are displayed in units of pounds and based on ${
            isUSA ? 'Allowable Stress Design' : 'Limit States Design'
          }`}</li>
          <li>
            Click on the Models above to be taken to the product page for more information, refer to
            the current Wood Construction Connectors catalog for&nbsp;
            <a
              target="_blank"
              href="http://www.strongtie.com/productuse/general-notes.html"
              rel="noreferrer"
            >
              General Notes
            </a>
            &nbsp;and&nbsp;
            <a
              target="_blank"
              href="http://www.strongtie.com/productuse/installer.html"
              rel="noreferrer"
            >
              Installation Instructions
            </a>
          </li>
        </ol>
      </div>
    </Fragment>
  )
}

export default Result
